import React, { MutableRefObject, useCallback, useEffect, useId, useState } from "react";
import * as cmd from "./Commands";
import { AbsType, BrakeSwitchMode, IdentResponse, WheelSpeedSource, WssConfig } from "./generated/wss_pb";

type DirPickerProps = Readonly<{
    text: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}>;

const DirPicker = ({ text, checked, onChange }: DirPickerProps): JSX.Element => {
    const id = useId();
    return (<div style={{ display: 'contents' }}>
        <label htmlFor={id} style={{ justifySelf: 'end' }}>{text}</label>
        <input id={id} type='checkbox' checked={checked} onChange={v => onChange(v.target.checked)} />
    </div>);
}

const SensorTypePicker = ({ text, checked, onChange }: DirPickerProps): JSX.Element => {
    const id = useId();
    return (<div style={{ display: 'contents' }}>
        <label htmlFor={id} style={{ justifySelf: 'end' }}>{text}</label>
        <select id={id} value={checked ? 1 : 0} onChange={c => onChange(+c.target.value !== 0)}>
            <option value={0}>VR</option>
            <option value={1}>Hall</option>
        </select>
    </div>);
}

type ConfiguratorProps = Readonly<{
    config: WssConfig;
    onWriteConfig: (config: WssConfig) => Promise<boolean>;
    onSelfTest: () => void;
    isMfgMode: boolean;
    onSetAbsType: (type: AbsType) => void;
    controllerIdent: IdentResponse;
}>;

const Configurator = ({ config, onWriteConfig, onSelfTest, isMfgMode, onSetAbsType, controllerIdent }: ConfiguratorProps) => {
    const [absType, setAbsType] = useState(config.getType());
    const absTypeId = useId();
    const speedSourceId = useId();
    const [writeStatus, setWriteStatus] = useState<'idle' | 'writing' | 'success' | 'error'>('idle');

    useEffect(() => {
        onSetAbsType(absType);
    }, [onSetAbsType, absType]);

    const [speedoPulsePerKm, setSpeedoPulsePerKm] = useState(config.getSpeedopulseperkm());
    const speedoPulsePerKmId = useId();

    const [dirLf, setDirLf] = useState(config.getDirlf());
    const [dirRf, setDirRf] = useState(config.getDirrf());
    const [dirLr, setDirLr] = useState(config.getDirlr());
    const [dirRr, setDirRr] = useState(config.getDirrr());

    const [sensorTypeFront, setSensorTypeFront] = useState(config.getFrontsensorshall());
    const [sensorTypeRear, setSensorTypeRear] = useState(config.getRearsensorshall());

    const [speedSource, setSpeedSource] = useState(config.getSpeedsource());

    const [invertFaultOutput, setInvertFaultOutput] = useState(config.getInvertfaultoutput());
    const [flashWhenAbsActive, setFlashWhenAbsActive] = useState(config.getFlashwhenabsactive());
    const [divideByTwo, setDivideByTwo] = useState(config.getDividebytwo());
    const [invertBrakeInput, setInvertBrakeInput] = useState(config.getInvertbrakeinput());

    const [brakeSwitchMode, setBrakeSwitchMode] = useState(config.getBrakeswitchmode());
    const brakeSwitchModeId = useId();

    const [canTransmitExt, setCanTransmitExt] = useState(config.getCantransmitext());
    const canExtId = useId();
    const [canTransmitId, setCanTransmitId] = useState(config.getCantransmitid());

    const [e90Send130, setE90Send130] = useState(config.getE90send130());
    const [e90SendDmeTorque, setE90SendDmeTorque] = useState(config.getE90senddmetorque());
    const [e90Send310, setE90Send310] = useState(config.getE90send310());
    const [e90Send330, setE90Send330] = useState(config.getE90send330());
    const [e90Send3b0, setE90Send3b0] = useState(config.getE90send3b0());
    const [e90SendDscOff, setE90SendDscOff] = useState(config.getE90senddscoff());

    const handleWrite = async () => {
        const cfg = new WssConfig();
        cfg.setType(absType);
        cfg.setSpeedopulseperkm(speedoPulsePerKm);
        cfg.setDirlf(dirLf);
        cfg.setDirrf(dirRf);
        cfg.setDirlr(dirLr);
        cfg.setDirrr(dirRr);
        cfg.setFrontsensorshall(sensorTypeFront);
        cfg.setRearsensorshall(sensorTypeRear);
        cfg.setSpeedsource(speedSource);
        cfg.setInvertfaultoutput(invertFaultOutput);
        cfg.setFlashwhenabsactive(flashWhenAbsActive);
        cfg.setDividebytwo(divideByTwo);
        cfg.setInvertbrakeinput(invertBrakeInput);
        cfg.setBrakeswitchmode(brakeSwitchMode);
        cfg.setCantransmitext(canTransmitExt);
        cfg.setCantransmitid(canTransmitId);
        cfg.setE90send130(e90Send130);
        cfg.setE90senddmetorque(e90SendDmeTorque);
        cfg.setE90send310(e90Send310);
        cfg.setE90send330(e90Send330);
        cfg.setE90send3b0(e90Send3b0);
        cfg.setE90senddscoff(e90SendDscOff);
        setWriteStatus('writing');
        const ok = await onWriteConfig(cfg);
        setWriteStatus(ok ? 'success' : 'error');
    };

    const hasDirectionOutput = absType === AbsType.MK60E5;
    const hasVehicleSpeed = (absType === AbsType.MK60) || (absType === AbsType.MK60E1) || (absType === AbsType.MK60E5);
    const hasFaultLight = (absType === AbsType.MK60E1) || (absType === AbsType.MK60E5);
    const hasCanBrakePressure = (absType === AbsType.MK60E1) || (absType === AbsType.MK60E5);
    const hasPtCanEmulation = (absType === AbsType.MK60E1) || (absType === AbsType.MK60E5);
    const hasSwitchableHallVr = controllerIdent.getHasswitchablehallvr();

    const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 12px', alignItems: 'center', justifyItems: 'start', justifyContent: 'start' };
    const sectionStyle: React.CSSProperties = { gridColumn: '1 / -1' };
    const labelStyle: React.CSSProperties = { justifySelf: 'end' };

    return (
        <div style={gridStyle}>
            <h3 style={sectionStyle}>Basic config</h3>
            <label htmlFor={absTypeId} style={labelStyle}>ABS controller type:</label>
            <select id={absTypeId} value={absType} onChange={c => setAbsType(+c.target.value)}>
                <option value={AbsType.MK60}>Mk60</option>
                <option value={AbsType.MK60E1}>Mk60e1</option>
                <option value={AbsType.MK60E5}>Mk60e5</option>
                <option value={AbsType.GENERICONOFF}>Generic On-Off Hall</option>
            </select>

            <DirPicker text='Divide speed by 2 (96 tooth wheels)' checked={divideByTwo} onChange={setDivideByTwo} />

            {hasVehicleSpeed &&
                (<>
                    <h3 style={sectionStyle}>Speedometer output</h3>
                    <label htmlFor={speedoPulsePerKmId} style={labelStyle}>Speedometer output pulse/km:</label>
                    <input type='number' id={speedoPulsePerKmId} value={speedoPulsePerKm} onChange={v => setSpeedoPulsePerKm(v.target.valueAsNumber)} />

                    <label htmlFor={speedSourceId} style={labelStyle}>Speedometer source:</label>
                    <select id={speedSourceId} value={speedSource} onChange={c => setSpeedSource(+c.target.value)}>
                        <option value={WheelSpeedSource.VSS}>Use vehicle speed (calc by ABS)</option>
                        <option value={WheelSpeedSource.AVERAGEALL4}>Average all 4 wheels</option>
                        <option value={WheelSpeedSource.AVERAGEFRONTS}>Average front wheels</option>
                        <option value={WheelSpeedSource.AVERAGEREARS}>Average rear wheels</option>
                    </select>
                </>)}

            {hasSwitchableHallVr &&
                (<>
                    <h3 style={sectionStyle}>Wheel speed sensor type</h3>
                    <SensorTypePicker text='Front' checked={sensorTypeFront} onChange={setSensorTypeFront} />
                    <SensorTypePicker text='Rear' checked={sensorTypeRear} onChange={setSensorTypeRear} />
                </>)}

            {hasDirectionOutput &&
                (<>
                    <h3 style={sectionStyle}>Wheel directions</h3>
                    <DirPicker text='Left front' checked={dirLf} onChange={setDirLf} />
                    <DirPicker text='Right front' checked={dirRf} onChange={setDirRf} />
                    <DirPicker text='Left rear' checked={dirLr} onChange={setDirLr} />
                    <DirPicker text='Right rear' checked={dirRr} onChange={setDirRr} />
                </>)}

            {hasFaultLight && (<>
                <h3 style={sectionStyle}>Fault light</h3>
                <DirPicker text='Invert fault light output' checked={invertFaultOutput} onChange={setInvertFaultOutput} />
                <DirPicker text='Flash fault light when ABS/DSC active' checked={flashWhenAbsActive} onChange={setFlashWhenAbsActive} />
            </>)}

            <h3 style={sectionStyle}>Brake switch input</h3>
            <DirPicker text='Invert brake switch' checked={invertBrakeInput} onChange={setInvertBrakeInput} />

            {hasCanBrakePressure && (<>
                <label htmlFor={brakeSwitchModeId} style={labelStyle}>Brake switch mode:</label>
                <select id={brakeSwitchModeId} value={brakeSwitchMode} onChange={c => setBrakeSwitchMode(+c.target.value)}>
                    <option value={BrakeSwitchMode.HARDWAREINPUT}>Hardware input</option>
                    <option value={BrakeSwitchMode.CANPRESSURE}>CAN brake pressure (E90)</option>
                </select>
            </>)}

            <h3 style={sectionStyle}>CAN</h3>
            <DirPicker text='CAN transmit extended' checked={canTransmitExt} onChange={setCanTransmitExt} />
            <label htmlFor={canExtId} style={labelStyle}>CAN transmit ID:</label>
            <input type='number' id={canExtId} value={canTransmitId} onChange={v => setCanTransmitId(v.target.valueAsNumber)} />

            {hasPtCanEmulation && (<>
                <h3 style={sectionStyle}>Emulated PT-CAN frames</h3>
                <DirPicker text='0x130 terminal status' checked={e90Send130} onChange={setE90Send130} />
                <DirPicker text='0x0A8/0x0A9/0x0AA DME torque' checked={e90SendDmeTorque} onChange={setE90SendDmeTorque} />
                <DirPicker text='0x310 outside temperature' checked={e90Send310} onChange={setE90Send310} />
                <DirPicker text='0x330 odometer' checked={e90Send330} onChange={setE90Send330} />
                <DirPicker text='0x3B0 reverse gear' checked={e90Send3b0} onChange={setE90Send3b0} />
                <DirPicker text='0x398 DSC disable' checked={e90SendDscOff} onChange={setE90SendDscOff} />
            </>)}

            <div style={sectionStyle}>
                <button type='button' onClick={handleWrite} disabled={writeStatus === 'writing'}>
                    {writeStatus === 'writing' ? 'Writing...' : 'Write config'}
                </button>
                {writeStatus === 'success' && <span style={{ color: 'green', marginLeft: 8 }}>Config saved</span>}
                {writeStatus === 'error' && <span style={{ color: 'red', marginLeft: 8 }}>Write failed</span>}
            </div>

            {false && isMfgMode && (
                <div style={sectionStyle}>
                    <button type='button' onClick={onSelfTest}>Run self test</button>
                </div>
            )}
        </div>);
};

type Props = Readonly<{
    port: SerialPort;
    onDisconnect: (reason: string) => void;
    absTypeRef: MutableRefObject<AbsType>;
    isMfgMode: boolean;
    controllerIdent: IdentResponse;
}>;

export const Settings = ({ port, onDisconnect, absTypeRef, isMfgMode, controllerIdent }: Props): JSX.Element => {
    const [config, setConfig] = useState<WssConfig | undefined>(undefined);

    // Read config from controller upon first render
    useEffect(() => {
        (async () => {
            const result = await cmd.getConfig(port, onDisconnect);

            if (result?.hasConfiguration()) {
                setConfig(result.getConfiguration());
            }
        })();
    }, [port, onDisconnect]);

    const onWriteConfig = useCallback(async (cfg: WssConfig): Promise<boolean> => {
        if (cfg) {
            try {
                const result = await cmd.setConfig(port, onDisconnect, cfg);
                return result != null;
            } catch {
                return false;
            }
        }
        return false;
    }, [port, onDisconnect]);

    const onSelfTest = async () => {
        await cmd.runSelfTest(port, onDisconnect);
    };

    return (<>
        <h2>Configuration</h2>
        {config && (<><Configurator config={config} onWriteConfig={onWriteConfig} onSelfTest={onSelfTest} isMfgMode onSetAbsType={(type) => absTypeRef.current = type} controllerIdent={controllerIdent} /> <p /></>)}
    </>);
};
